import { checkPermissionMiddleware } from '../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission } from '../../permissions/roles.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { UserAPI } from '../../users/api/UserAPI.js';
import type { Abwesenheit } from '../abwesenheit.js';
import { AbbreviationReasonsForAbsence, PartOfDayEnum } from '../enums/abwesenheiten.js';

/**
 * Entity for import Abwesenheiten daten
 */
@Entity()
export class AbwesenheitenImport extends MongoEntity {
  static override _class = 'AbwesenheitenImport';

  @Field(type => [String])
  lesson?: number[];

  @Field(type => String)
  reasonAbbreviation?: string;

  @Field(type => String)
  name: Maybe<string>;

  static createAbwesenheit = async (
    start: Date,
    ende: Date,
    ganztaegig: boolean,
    partOfTheDay: string,
    personID: string,
    abwesenheitClass: { reasonAbbreviation: string; entityClass: string },
  ) => {
    const EntityClass = baseqlMetaData.entities.resolveNode(abwesenheitClass.entityClass).type() as typeof Abwesenheit;
    const abwesenheit = new EntityClass({
      _id: A.getGloballyUniqueID(),
      personID,
      start,
      ende,
      ganztaegig,
      partOfTheDay,
    });
    await abwesenheit.create();
    return abwesenheit;
  };

  static getBreakTimenByLesson = (lessons, year: string, month: string, day: string) => {
    // Number 5 is the last stunde of the morning.
    const isMorningBreak: boolean = lessons.some(lesson => lesson <= 5);
    const ganztaegig: boolean = false;
    const partOfTheDay: PartOfDayEnum = isMorningBreak ? PartOfDayEnum.VORMITTAG : PartOfDayEnum.NACHMITTAG;
    // die Abwesenheit umfasst genau diesen Kalendertag, vor- bzw. nachmittags steckt in partOfTheDay
    const tag = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return {
      startTime: tag,
      endTime: tag,
      partOfTheDay,
      ganztaegig,
    };
  };

  static getReasonByReasonAbbreviation = (reasonAbbreviation: string) => {
    const entityClass = AbbreviationReasonsForAbsence[reasonAbbreviation] ?? '';
    return { reasonAbbreviation, entityClass };
  };
}

export const importAbwesenheiten = registerMutation({
  operationName: 'importAbwesenheiten',
  middlewares: [checkPermissionMiddleware(Permission.AN_SEKTION_W)],
  resolve: (
    abwesenheitenImportDaten: AbwesenheitenImport[],
    nameAbbreviations: string[],
    dayMatch: string,
    monthMatch: string,
    yearMatch: string,
  ) => {
    return async (gqlFields: void) => {
      const personen = await UserAPI.getPersonsByNameAbbreviations(nameAbbreviations)({ nameAbbreviation: 1 });
      // sequential, not forEach(async …): those callbacks float, so the mutation reported success
      // before a single absence was written and every rejection escaped as an unhandled one
      for (const person of personen) {
        for (const abwesenheit of abwesenheitenImportDaten ?? []) {
          if (abwesenheit.name !== person.nameAbbreviation) continue;

          const { startTime, endTime, ganztaegig, partOfTheDay } = AbwesenheitenImport.getBreakTimenByLesson(
            abwesenheit?.lesson,
            yearMatch,
            monthMatch,
            dayMatch,
          );

          try {
            await AbwesenheitenImport.createAbwesenheit(
              startTime,
              endTime,
              ganztaegig,
              partOfTheDay,
              person._id,
              AbwesenheitenImport.getReasonByReasonAbbreviation(abwesenheit?.reasonAbbreviation ?? ''),
            );
          } catch (error) {
            logger.info(`Import Abwesenheit: ${error.message}`);
          }
        }
      }
    };
  },
  type: () => Boolean,
  params: [
    { name: 'abwesenheitDaten', type: type => [AbwesenheitenImport] },
    { name: 'nameAbbreviations', type: type => [String] },
    { name: 'dayMatch', type: type => String },
    { name: 'monthMatch', type: type => String },
    { name: 'yearMatch', type: type => String },
  ],
});
